import React from 'react';
import { Document, Font, Image, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import skillcertLogo from './assets/skillcert-logo.png';

const styles = StyleSheet.create({
  page: {
    paddingTop: 72,
    paddingBottom: 28,
    paddingHorizontal: 28,
    fontSize: 9.4,
    color: '#4D4D4D',
    lineHeight: 1.34,
    fontFamily: 'Roboto',
  },
  header: {
    position: 'absolute',
    top: 16,
    left: 28,
    right: 28,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#D8D5E7',
    paddingBottom: 8,
  },
  logo: {
    width: 96,
    height: 18,
    objectFit: 'contain',
  },
  metaCard: {
    borderWidth: 1,
    borderColor: '#D8D5E7',
    borderRadius: 6,
    padding: 7,
    marginBottom: 7,
    backgroundColor: '#F9F8FD',
  },
  metaTitle: {
    fontSize: 8.2,
    color: '#696969',
    marginBottom: 1,
  },
  metaValue: {
    fontSize: 9.6,
    color: '#1F1C31',
    fontWeight: 700,
    marginBottom: 3,
  },
  recommendationPill: {
    alignSelf: 'flex-start',
    borderRadius: 99,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginBottom: 4,
  },
  recommendationPillText: {
    fontSize: 8,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  reportTitle: {
    fontSize: 12.8,
    color: '#3F3861',
    fontWeight: 700,
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 11.2,
    color: '#332C54',
    fontWeight: 700,
    marginTop: 6,
    marginBottom: 4,
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderWidth: 1,
    borderColor: '#DAD5EA',
    borderRadius: 4,
    backgroundColor: '#F4F1FB',
  },
  sectionSpacing: {
    marginBottom: 3,
  },
  projectionSectionSpacing: {
    marginBottom: 5,
  },
  sectionBodyDivider: {
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#EDEAF8',
  },
  projectionDisclaimerBox: {
    borderWidth: 1,
    borderColor: '#E5DBA8',
    backgroundColor: '#FFF8DD',
    borderRadius: 6,
    padding: 6,
    marginBottom: 6,
  },
  projectionDisclaimerTitle: {
    fontSize: 8.7,
    color: '#5A4E2A',
    fontWeight: 700,
    marginBottom: 2,
  },
  projectionDisclaimerBody: {
    fontSize: 8.2,
    color: '#5A4E2A',
    lineHeight: 1.24,
  },
  h2: {
    fontSize: 10.2,
    color: '#3F3861',
    fontWeight: 700,
    marginTop: 4,
    marginBottom: 3,
    paddingBottom: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#EDECF4',
  },
  h3: {
    fontSize: 9.4,
    color: '#564C85',
    fontWeight: 700,
    marginTop: 5,
    marginBottom: 2,
  },
  paragraph: {
    marginBottom: 2,
    color: '#4D4D4D',
  },
  bullet: {
    marginBottom: 1,
    marginLeft: 8,
  },
  divider: {
    marginVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#EDECF4',
  },
  footer: {
    position: 'absolute',
    left: 28,
    right: 28,
    bottom: 10,
    fontSize: 8,
    color: '#696969',
    textAlign: 'right',
  },
  factualCard: {
    borderWidth: 1,
    borderColor: '#D8D5E7',
    borderRadius: 6,
    backgroundColor: '#F7F5FF',
    padding: 6,
    marginTop: 5,
  },
  factualTitle: {
    fontSize: 8.8,
    color: '#3F3861',
    fontWeight: 700,
    marginBottom: 3,
  },
  factualBullet: {
    fontSize: 8.1,
    color: '#4D4D4D',
    lineHeight: 1.28,
    marginBottom: 2,
  },
  batchCandidateBanner: {
    borderWidth: 1,
    borderColor: '#D8D5E7',
    borderRadius: 6,
    backgroundColor: '#F7F4FE',
    paddingVertical: 4,
    paddingHorizontal: 6,
    marginBottom: 6,
  },
  batchCandidateBannerText: {
    color: '#3F3861',
    fontSize: 8.8,
    fontWeight: 700,
  },
});

let fontsReady = false;

function resolveLocale(locale) {
  return locale === 'en-US' ? 'en-US' : 'pt-BR';
}

function getPdfLabels(locale) {
  const current = resolveLocale(locale);
  if (current === 'en-US') {
    return {
      page: 'Page',
      of: 'of',
      candidate: 'Candidate',
      candidates: 'Candidates',
      notInformed: 'Not informed',
      type: 'Type',
      position: 'Position',
      recommendation: 'Recommendation',
      generatedAt: 'Generated at',
      comparativeReport: 'Comparative Report',
      individualReport: 'Individual Report',
      comparativeAnalysis: 'Comparative Analysis',
      decisorySynthesis: 'Decisory Synthesis',
      analyticalSynthesis: 'Analytical Synthesis',
      narrativeProjection: 'Narrative Projection',
      factualBasisHeading: 'Factual basis of this projection',
      projectionDisclaimerTitle: 'How to read this projection',
      projectionDisclaimerBody: 'This text is a narrative tangibilization based on analysis data, role context, and company principles. Language is intentionally vivid and prospective. It is not deterministic prediction.',
      report: 'SkillCert Report',
      reportFile: 'skillcert_report',
    };
  }
  return {
    page: 'Página',
    of: 'de',
    candidate: 'Candidato',
    candidates: 'Candidatos',
    notInformed: 'Não informado',
    type: 'Tipo',
    position: 'Posição',
    recommendation: 'Recomendação',
    generatedAt: 'Gerado em',
    comparativeReport: 'Relatório Comparativo',
    individualReport: 'Relatório Individual',
    comparativeAnalysis: 'Análise Comparativa',
    decisorySynthesis: 'Síntese Decisória',
    analyticalSynthesis: 'Síntese Analítica',
    narrativeProjection: 'Projeção Narrativa',
    factualBasisHeading: 'Base factual desta projeção',
    projectionDisclaimerTitle: 'Como ler esta projeção',
    projectionDisclaimerBody: 'Este texto é uma tangibilização narrativa baseada nos dados da análise, no contexto da vaga e nos princípios da empresa. A linguagem é propositalmente mais viva e prospectiva. Não é previsão determinística.',
    report: 'Relatorio SkillCert',
    reportFile: 'relatorio_skillcert',
  };
}

function getRecommendationBadgeColors(recommendationClass) {
  const token = String(recommendationClass || '').trim();
  if (token === 'badge-green') return { backgroundColor: '#EAF7EE', color: '#1F7A3A' };
  if (token === 'badge-blue') return { backgroundColor: '#EAF0FB', color: '#1A4FA0' };
  if (token === 'badge-yellow') return { backgroundColor: '#FFF8E6', color: '#A0620A' };
  if (token === 'badge-red') return { backgroundColor: '#FDECEC', color: '#C0392B' };
  return { backgroundColor: '#EFEAFB', color: '#3F3861' };
}

function splitNarrativeProjectionForPdf(text, locale) {
  const labels = getPdfLabels(locale);
  const headings = [
    labels.factualBasisHeading || (resolveLocale(locale) === 'en-US' ? 'Factual basis of this projection' : 'Base factual desta projeção'),
    'Reference Notes',
    'Notas de referência',
  ];
  const headingRegex = new RegExp(`##\\s+(${headings.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\s*`, 'i');
  const source = String(text || '').trim();
  if (!source) return { narrativeText: '', factualHeading: labels.factualBasisHeading, factualBullets: [] };

  const match = source.match(headingRegex);
  if (!match || match.index === undefined) {
    return {
      narrativeText: source,
      factualHeading: labels.factualBasisHeading,
      factualBullets: [],
    };
  }

  const narrativeText = source.slice(0, match.index).trim();
  const factualRaw = source.slice(match.index + match[0].length).trim();
  const factualBullets = factualRaw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('-'))
    .map((line) => stripMarkdown(line.replace(/^-\s+/, '')))
    .filter(Boolean)
    .slice(0, 3);

  return {
    narrativeText,
    factualHeading: labels.factualBasisHeading,
    factualBullets,
  };
}

function ensureFontsRegistered() {
  if (fontsReady) return;
  const origin = window.location.origin;
  Font.register({
    family: 'Roboto',
    fonts: [
      { src: `${origin}/fonts/Roboto-Regular.ttf`, fontWeight: 400 },
      { src: `${origin}/fonts/Roboto-Bold.ttf`, fontWeight: 700 },
    ],
  });
  fontsReady = true;
}

function stripMarkdown(text) {
  return (text || '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .trim();
}

function markdownToBlocks(text) {
  if (!text) return [];

  const lines = text.split('\n');
  const blocks = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    if (line === '---') {
      blocks.push({ type: 'hr' });
      continue;
    }
    if (line.startsWith('### ')) {
      blocks.push({ type: 'h3', text: stripMarkdown(line.slice(4)) });
      continue;
    }
    if (line.startsWith('## ')) {
      blocks.push({ type: 'h2', text: stripMarkdown(line.slice(3)) });
      continue;
    }
    if (/^(?:[-•→]|\d+\.)\s+/.test(line)) {
      blocks.push({ type: 'li', text: stripMarkdown(line.replace(/^(?:[-•→]|\d+\.)\s+/, '')) });
      continue;
    }

    blocks.push({ type: 'p', text: stripMarkdown(line) });
  }

  return blocks;
}

function MarkdownBlocks({ text }) {
  return markdownToBlocks(text).map((block, index) => {
    if (block.type === 'h2') return <Text key={index} style={styles.h2}>{block.text}</Text>;
    if (block.type === 'h3') return <Text key={index} style={styles.h3}>{block.text}</Text>;
    if (block.type === 'li') return <Text key={index} style={styles.bullet}>• {block.text}</Text>;
    if (block.type === 'hr') return <View key={index} style={styles.divider} />;
    return <Text key={index} style={styles.paragraph}>{block.text}</Text>;
  });
}

function ReportHeader({ logoSrc }) {
  return (
    <View style={styles.header} fixed>
      <Image src={logoSrc} style={styles.logo} />
    </View>
  );
}

function ReportFooter({ locale }) {
  const labels = getPdfLabels(locale);
  return (
    <Text
      style={styles.footer}
      render={({ pageNumber, totalPages }) => `SkillCert · ${labels.page} ${pageNumber} ${labels.of} ${totalPages}`}
      fixed
    />
  );
}

function MetaCard({ report, typeLabel }) {
  const labels = getPdfLabels(report.locale);
  const nomes = report.candidateNames || [];
  const candidatosLabel = nomes.length === 1 ? labels.candidate : labels.candidates;
  const candidatosValue = nomes.length > 0 ? nomes.join(' · ') : labels.notInformed;
  const recommendationColors = getRecommendationBadgeColors(report.recommendationClass);

  return (
    <View style={styles.metaCard}>
      <Text style={styles.metaTitle}>{labels.type}</Text>
      <Text style={styles.metaValue}>{typeLabel}</Text>
      <Text style={styles.metaTitle}>{labels.position}</Text>
      <Text style={styles.metaValue}>{report.positionTitle || labels.notInformed}</Text>
      <Text style={styles.metaTitle}>{candidatosLabel}</Text>
      <Text style={styles.metaValue}>{candidatosValue}</Text>
      {report.recommendationLabel ? (
        <>
          <Text style={styles.metaTitle}>{labels.recommendation}</Text>
          <View style={[styles.recommendationPill, { backgroundColor: recommendationColors.backgroundColor }]}>
            <Text style={[styles.recommendationPillText, { color: recommendationColors.color }]}>
              {report.recommendationLabel}
            </Text>
          </View>
        </>
      ) : null}
      <Text style={styles.metaTitle}>{labels.generatedAt}</Text>
      <Text style={styles.metaValue}>{report.generatedAtLabel}</Text>
    </View>
  );
}

function NarrativeProjectionSection({ text, locale }) {
  const labels = getPdfLabels(locale);
  const { narrativeText, factualHeading, factualBullets } = splitNarrativeProjectionForPdf(text, locale);
  return (
    <View style={styles.projectionSectionSpacing}>
      <Text style={styles.sectionTitle}>{labels.narrativeProjection}</Text>
      <View style={styles.projectionDisclaimerBox}>
        <Text style={styles.projectionDisclaimerTitle}>{labels.projectionDisclaimerTitle}</Text>
        <Text style={styles.projectionDisclaimerBody}>{labels.projectionDisclaimerBody}</Text>
      </View>
      <MarkdownBlocks text={narrativeText || text} />
      {factualBullets.length > 0 ? (
        <View style={styles.factualCard} wrap={false}>
          <Text style={styles.factualTitle}>{factualHeading}</Text>
          {factualBullets.map((item, idx) => (
            <Text key={`${idx}-${item.slice(0, 20)}`} style={styles.factualBullet}>• {item}</Text>
          ))}
        </View>
      ) : null}
    </View>
  );
}

function SkillCertSingleReportPage({ report, logoSrc }) {
  const labels = getPdfLabels(report.locale);
  const typeLabel = report.type === 'comparative' ? labels.comparativeReport : labels.individualReport;

  return (
    <Page size="A4" style={styles.page} wrap>
      <ReportHeader logoSrc={logoSrc} />
      <Text style={styles.reportTitle}>{typeLabel}</Text>
      <MetaCard report={report} typeLabel={typeLabel} />

      {report.type === 'comparative' ? (
        <View style={styles.sectionSpacing}>
          <Text style={styles.sectionTitle}>{labels.comparativeAnalysis}</Text>
          <View style={styles.sectionBodyDivider} />
          <MarkdownBlocks text={report.comparative} />
        </View>
      ) : (
        <>
          <View style={styles.sectionSpacing}>
            <Text style={styles.sectionTitle}>{labels.decisorySynthesis}</Text>
            <View style={styles.sectionBodyDivider} />
            <MarkdownBlocks text={report.decisory} />
          </View>
          <View style={styles.sectionSpacing}>
            <Text style={styles.sectionTitle}>{labels.analyticalSynthesis}</Text>
            <View style={styles.sectionBodyDivider} />
            <MarkdownBlocks text={report.analytical} />
          </View>
          {report.narrativeProjection ? (
            <NarrativeProjectionSection text={report.narrativeProjection} locale={report.locale} />
          ) : null}
        </>
      )}

      <ReportFooter locale={report.locale} />
    </Page>
  );
}

function SkillCertBatchPage({ item, logoSrc, itemIndex = 1, totalItems = 1 }) {
  const labels = getPdfLabels(item.locale);
  const report = {
    ...item,
    type: 'individual',
    candidateNames: [item.candidateName],
  };
  return (
    <Page size="A4" style={styles.page} wrap>
      <ReportHeader logoSrc={logoSrc} />
      <Text style={styles.reportTitle}>
        {`${labels.individualReport} — ${item.candidateName || labels.candidate}`}
      </Text>
      <View style={styles.batchCandidateBanner}>
        <Text style={styles.batchCandidateBannerText}>
          {`${labels.candidate} ${itemIndex} ${labels.of} ${totalItems}: ${item.candidateName || labels.notInformed}`}
        </Text>
      </View>
      <MetaCard report={report} typeLabel={labels.individualReport} />

      <View style={styles.sectionSpacing}>
        <Text style={styles.sectionTitle}>{labels.decisorySynthesis}</Text>
        <View style={styles.sectionBodyDivider} />
        <MarkdownBlocks text={item.decisory} />
      </View>
      <View style={styles.sectionSpacing}>
        <Text style={styles.sectionTitle}>{labels.analyticalSynthesis}</Text>
        <View style={styles.sectionBodyDivider} />
        <MarkdownBlocks text={item.analytical} />
      </View>
      {item.narrativeProjection ? (
        <NarrativeProjectionSection text={item.narrativeProjection} locale={item.locale} />
      ) : null}

      <ReportFooter locale={item.locale} />
    </Page>
  );
}

function buildGeneratedAtLabel(locale) {
  return new Date().toLocaleString(resolveLocale(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function sanitizeFileName(value) {
  return (value || 'relatorio')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100);
}

export async function exportSkillCertPdf(rawReport) {
  ensureFontsRegistered();
  const labels = getPdfLabels(rawReport.locale);

  const report = {
    ...rawReport,
    generatedAtLabel: rawReport.generatedAtLabel || buildGeneratedAtLabel(rawReport.locale),
  };

  const logoSrc = skillcertLogo;
  const doc = report.type === 'individual_batch'
    ? (
      <Document title={report.fileName || labels.report} author="SkillCert">
        {(report.items || []).map((item, index, arr) => (
          <SkillCertBatchPage
            key={`${item.candidateName}-${index}`}
            item={{ ...item, locale: report.locale }}
            logoSrc={logoSrc}
            itemIndex={index + 1}
            totalItems={arr.length}
          />
        ))}
      </Document>
    )
    : (
      <Document title={report.fileName || labels.report} author="SkillCert">
        <SkillCertSingleReportPage report={report} logoSrc={logoSrc} />
      </Document>
    );

  const blob = await pdf(doc).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${report.fileName || labels.reportFile}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
